import React, { useState, useEffect, useRef } from "react";
import {
  doc,
  setDoc,
  getDoc,
  updateDoc,
  serverTimestamp,
  arrayUnion,
} from "firebase/firestore";
import { db } from "./firebase";
import "katex/dist/katex.min.css";
import { BlockMath } from "react-katex";
import "mathlive";
import Confetti from "react-confetti";
import { useWindowSize } from "@react-hook/window-size";
import { useParams } from "react-router-dom";

/**
 * ✅ This version:
 * - Gives EVERY tile (20) its OWN unique question on simple + compound interest
 * - Does NOT depend on Firestore "questions" collection at all (no missing data issues)
 * - Uses numeric tolerance checking (so "1210", "1210.00", "£1210" etc can be marked correct)
 * - Still saves attempts into studentAssignmentSubmissions like your current system
 * - Still supports review mode (loads attempts back in)
 */

// Golden hex tiles (middle grid)
const tileGrid = [
  ["1", "2", "3", "4", "5"],
  ["6", "7", "8", "9", "10"],
  ["11", "12", "13", "14", "15"],
  ["16", "17", "18", "19", "20"],
];

// Firebase image URLs
const GOLD_TILE =
  "https://firebasestorage.googleapis.com/v0/b/ryan-tutors.firebasestorage.app/o/images%2Fhex_tile.png?alt=media&token=bac8036f-49f8-4333-90bf-f00ebfd7e2c0";

const BLUE_TILE =
  "https://firebasestorage.googleapis.com/v0/b/ryan-tutors.firebasestorage.app/o/images%2Fhex_tile_blue.png?alt=media&token=8b270b4b-ceea-482b-b7d8-e3d04a06c238";

const WHITE_TILE =
  "https://firebasestorage.googleapis.com/v0/b/ryan-tutors.firebasestorage.app/o/images%2Fhex_tile_white.png?alt=media&token=a2463879-326d-4664-895c-6bb8adf8fe59";

// ----------------------------
// ✅ Interest question bank (20 unique tiles)
// Each question supports numeric checking using expectedValue + tolerance.
// You can also add "answers" strings if you like (still supported).
// ----------------------------
const TILE_QUESTIONS = {
  // Row 0
  "0-0": {
    id: "si_01",
    questionParagraphs: [
      "£800 invested at 5% per year simple interest for 3 years.",
      "Find the total amount.",
    ],
    expectedValue: 800 * (1 + 0.05 * 3), // 920
    tolerance: 0.01,
    unitHint: "£",
  },
  "0-1": {
    id: "si_02",
    questionParagraphs: [
      "£1,200 invested at 4% per year simple interest for 5 years.",
      "How much interest is earned?",
    ],
    expectedValue: 1200 * 0.04 * 5, // 240
    tolerance: 0.01,
    unitHint: "£",
  },
  "0-2": {
    id: "si_03",
    questionParagraphs: [
      "A total of £575 is repaid after 2 years at 6% simple interest.",
      "What was the original amount borrowed?",
    ],
    // A = P(1+rt) => P = A/(1+rt)
    expectedValue: 575 / (1 + 0.06 * 2), // 575/1.12 = 513.392857...
    tolerance: 0.01,
    unitHint: "£",
  },
  "0-3": {
    id: "ci_01",
    questionParagraphs: [
      "£500 is invested 8% per year for 2 years.",
      "Find the final amount.",
    ],
    expectedValue: 500 * Math.pow(1.08, 2), // 583.2
    tolerance: 0.01,
    unitHint: "£",
  },
  "0-4": {
    id: "ci_02",
    questionParagraphs: [
      "£2448 is invested in an account at a rate of compound interest.",
      "One year after the investment there is £2496.96 in the account.",
      "How much is in the account four years after the investment?",
    ],
    expectedValue: 2649.79,
    tolerance: 0.01,
    unitHint: "£",
  },

  // Row 1
  "1-0": {
    id: "si_04",
    questionParagraphs: [
      "£950 invested at 7% simple interest per year.",
      "After how many years will it be worth £1,216.50?",
    ],
    // A = P(1+rt) => t = (A/P - 1)/r
    expectedValue: (1216.5 / 950 - 1) / 0.07,
    tolerance: 0.01,
    unitHint: "years",
  },
  "1-1": {
    id: "ci_03",
    questionParagraphs: [
      "Ryan invested £500 at a compound interest rate of r% per annum.",
      "After 2 years, he has £605.00 in his account.",
      "Find the value of r.",
    ],
    // 1000*(1+r)^3 = 1331 => 1+r = 1.1 => r = 0.1 => 10%
    expectedValue: 10,
    tolerance: 0,
    unitHint: "%",
  },
  "1-2": {
    id: "ci_04",
    questionParagraphs: [
      "£750 grows at a compound interest rate of 6% per year",
      "How much interest is earned after 5 years?",
    ],
    expectedValue: 750 * Math.pow(1.06, 5) - 750,
    tolerance: 0.01,
    unitHint: "£",
  },
  "1-3": {
    id: "ci_05",
    questionParagraphs: [
      "A laptop costs £900 and loses 12% of its value each year.",
      "What is its value after 3 years?",
    ],
    expectedValue: 900 * Math.pow(0.88, 3),
    tolerance: 0.01,
    unitHint: "£",
  },
  "1-4": {
    id: "si_05",
    questionParagraphs: [
      "Matt wants to invest £8000 for three years.",
       "He can choose between Bank A and Bank B.",
{
      type: "image",
      src: "/BlockBusters/CompoundInterest/CompoundInterest1.png",
      alt: "Compound interest growth diagram",
      maxWidth: "420px"
    },
      "Bank B gives Matt more interest than Bank A.",
      "How much more?",
    ],
    expectedValue: 32.55,
    tolerance: 0.02,
    unitHint: "£",
  },
   

  // Row 2
  "2-0": {
    id: "ci_06",
    questionParagraphs: [
      "£3,500 decreases by 8% each half-year.",
      "Find the value after 3 years.",
    ],
    // A = P(1 + r/12)^(12)
    expectedValue: 2122.24,
    tolerance: 0.01,
    unitHint: "£",
  },
  "2-1": {
    id: "ci_07",
    questionParagraphs: [
      "£1,500 grows by 4.5% per year for 7 years.",
      "Find the final amount.",
    ],
    expectedValue: 2041.29,
    tolerance: 0.01,
    unitHint: "£",
  },
  "2-2": {
    id: "si_06",
    questionParagraphs: [
      "A loan of £2,400 charges 7.5% simple interest per year.",
      "The interest is £540.",
      "How long was the loan for (in years)?",
    ],
    // I = Prt => t = I/(Pr)
    expectedValue: 540 / (2400 * 0.075),
    tolerance: 0.01,
    unitHint: "years",
  },
  "2-3": {
    id: "ci_08",
    questionParagraphs: [
      "An investment increases by 2% each year.",
      "Write the multiplier used each year.",
    ],
    expectedValue: 1.02,
    tolerance: 0.0005,
    unitHint: "",
  },
  "2-4": {
    id: "ci_09",
    questionParagraphs: [
      "£3250 is invested in an account paying compound interest.",
      "One year later, the amount in the account is £3347.50.",
      "How much is in the account after 5 years?",
    ],
    expectedValue: 3767.64,
    tolerance: 0.01,
    unitHint: "£",
  },
    

  // Row 3
  "3-0": {
    id: "si_07",
    questionParagraphs: [
      "£350 invested at 3% simple interest per year for 9 years.",
      "Find the final amount.",
    ],
    expectedValue: 350 * (1 + 0.03 * 9),
    tolerance: 0.01,
    unitHint: "£",
  },
  "3-1": {
    id: "ci_10",
    questionParagraphs: [
      "Ryan invested £2,500 at 7% compound interest per year for 3 years.",
      "Find the final amount.",
    ],
    expectedValue: 2500 * Math.pow(1.07, 3),
    tolerance: 0.01,
    unitHint: "£",
  },
  "3-2": {
    id: "ci_11",
    questionParagraphs: [
      "£1,000 grows at 5% per year.",
      "After how many years will it first exceed £1,276.28?",
      "(Give the whole number of years.)",
    ],
    // 1000*(1.05)^t > 1276.28 => t > log(1.27628)/log(1.05)
    expectedValue: Math.ceil(Math.log(1276.28 / 1000) / Math.log(1.05)),
    tolerance: 0.0001,
    unitHint: "years",
    integerOnly: true,
  },
  "3-3": {
    id: "si_08",
    questionParagraphs: [
      "A car worth £9,000 depreciates by 15% each year.",
      "Find its value after 2 years.",
    ],
    expectedValue: 6502.50,
    tolerance: 0.01,
    unitHint: "£",
  },
  "3-4": {
    id: "ci_12",
    questionParagraphs: [
      "Mia wants to borrow £6000 and repay it, with interest, after two years.",
       "She sees two offers for loans.",
{
      type: "image",
      src: "/BlockBusters/CompoundInterest/CompoundInterest2.png",
      alt: "Compound interest growth diagram",
      maxWidth: "420px"
    },
      "Calculate the difference in the total amount repaid between the two offers after two years.",
    ],
    expectedValue: 2.40,
    tolerance: 0.02,
    unitHint: "£",
  },
};



// ----------------------------
// Helpers
// ----------------------------
const isLatex = (s) =>
  typeof s === "string" && /\\|\\frac|\\left|\\right|\$/.test(s);

const normalizeText = (s = "") =>
  String(s).trim().toLowerCase().replace(/\s+/g, "");

const extractNumberFromMathlive = (raw = "") => {
  // raw is usually mathlive value (often LaTeX-like)
  // We'll strip everything except digits, decimal, minus, commas.
  const s = String(raw)
    .replace(/\\,|\\pounds|£/g, "")
    .replace(/\\text\{[^}]*\}/g, "")
    .replace(/[^0-9.,-]/g, "");

  // remove commas
  const cleaned = s.replace(/,/g, "");

  // if multiple numbers exist, try parse the last meaningful one
  const match = cleaned.match(/-?\d+(\.\d+)?/g);
  if (!match) return null;

  const last = match[match.length - 1];
  const n = Number(last);
  return Number.isFinite(n) ? n : null;
};

const nearlyEqual = (a, b, tol) => Math.abs(a - b) <= tol;

function BlockbustersGrid() {
  const [showModal, setShowModal] = useState(false);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [attemptIndex, setAttemptIndex] = useState(0);
  const [playerAnswer, setPlayerAnswer] = useState("");
  const [playerWorking, setPlayerWorking] = useState("");
  const questionRef = useRef(null);
  const [clickedTile, setClickedTile] = useState(null);
  const [questionHeight, setQuestionHeight] = useState(200);
  const [feedback, setFeedback] = useState(null); // 'correct' | 'incorrect' | null
  const [selectedTile, setSelectedTile] = useState(null);
  const [answeredTiles, setAnsweredTiles] = useState([]);
  const [failedTiles, setFailedTiles] = useState([]);
  const [attempts, setAttempts] = useState(0);
  const [hasWon, setHasWon] = useState(false);
  const [width, height] = useWindowSize();
  const [reviewMode, setReviewMode] = useState(false);
  const [answeredQuestions, setAnsweredQuestions] = useState([]);
  const [stage, setStage] = useState("welcome");
const audioRef = useRef(null);
const [musicPlaying, setMusicPlaying] = useState(true);
useEffect(() => {
  const audio = audioRef.current;
  if (!audio) return;

  // 🎚️ Volume depends on stage
  if (stage === "welcome") {
    audio.volume = 0.03; // louder on welcome screen
  } else {
    audio.volume = 0.007; // half volume on game board
  }

  // ▶️ Play only if music is on AND no question modal open
  if (musicPlaying && !showModal) {
    const playPromise = audio.play();
    if (playPromise?.catch) {
      playPromise.catch(() => {});
    }
  } else {
    audio.pause();
  }
}, [stage, musicPlaying, showModal]);




  const { assignmentId } = useParams();
  const queryParams = new URLSearchParams(window.location.search);
  const studentUID = queryParams.get("studentUID");
  const mathFieldRef = useRef(null);

  // ----------------------------
  // Load/create submission + load review attempts
  // ----------------------------
  
  useEffect(() => {
    const init = async () => {
      if (!assignmentId || !studentUID) {
        console.error("❌ Missing assignmentId or studentUID");
        return;
      }

      const submissionId = `${studentUID}_${assignmentId}`;
      const submissionRef = doc(db, "studentAssignmentSubmissions", submissionId);

      try {
        const snap = await getDoc(submissionRef);

        if (!snap.exists()) {
          await setDoc(submissionRef, {
            assignmentId,
            studentUID,
            type: "blockbusters",
            timeStarted: serverTimestamp(),
            timeSubmitted: null,
            answers: [],
          });
          console.log("📝 Created new submission doc");
        } else {
          console.log("📄 Submission already exists");
          const data = snap.data();
          const answers = Array.isArray(data.answers) ? data.answers : [];

          if (answers.length > 0) {
            const correctTiles = answers
              .filter((a) => a.correct)
              .map((a) => a.tileKey);
            const wrongTiles = answers
              .filter((a) => a.correct === false)
              .map((a) => a.tileKey);

            setAnsweredTiles(correctTiles);
            setFailedTiles(wrongTiles);

            // Enrich attempts with question content from local bank
            const enriched = answers.map((entry) => ({
              ...entry,
              question:
                TILE_QUESTIONS[entry.tileKey] ||
                entry.question ||
                ({
                  id: "missing",
                  questionParagraphs: ["Question not found for this tileKey."],
                }),
              tileValue:
                entry.tileValue ||
                tileGrid?.[parseInt(entry.tileKey?.split("-")[0], 10)]?.[
                  parseInt(entry.tileKey?.split("-")[1], 10)
                ],
            }));

            setAnsweredQuestions(enriched);
            setReviewMode(true);
          }
        }
      } catch (err) {
        console.error("❌ init error:", err);
      }
    };

    init();
  }, [assignmentId, studentUID]);

  // Keep modal height synced
  useEffect(() => {
    if (showModal && questionRef.current) {
      setQuestionHeight(questionRef.current.offsetHeight);
    }
  }, [showModal, currentQuestion]);

  // When switching attempt in review mode, update modal view
  useEffect(() => {
    if (!reviewMode || !selectedTile) return;

    const tileKey = `${selectedTile.row}-${selectedTile.col}`;
    const attemptsForTile = answeredQuestions.filter((q) => q.tileKey === tileKey);
    const currentAttempt = attemptsForTile[attemptIndex];

    if (currentAttempt) {
      setCurrentQuestion(currentAttempt.question);
      setPlayerAnswer(currentAttempt.playerAnswer || "—");
      setPlayerWorking(currentAttempt.playerWorking || "—");
      setFeedback(currentAttempt.correct ? "correct" : "incorrect");
    }
  }, [attemptIndex, selectedTile, reviewMode, answeredQuestions]);

  // When modal opens in review mode, push answer into mathlive
  useEffect(() => {
    if (reviewMode && showModal && mathFieldRef.current && playerAnswer) {
      try {
        mathFieldRef.current.setValue(playerAnswer);
      } catch {
        // ignore
      }
    }
  }, [reviewMode, showModal, playerAnswer, attemptIndex]);

  // ----------------------------
  // Win check (your existing DFS logic, unchanged)
  // ----------------------------
  const checkForWin = (tiles) => {
    const visited = new Set();

    const evenRowDirs = [
      [-1, 0],
      [-1, 1],
      [0, -1],
      [0, 1],
      [1, 0],
      [1, 1],
      [-1, -1],
      [1, -1],
    ];

    const oddRowDirs = [
      [-1, -1],
      [-1, 0],
      [0, -1],
      [0, 1],
      [1, -1],
      [1, 0],
      [-1, 1],
      [1, 1],
    ];

    const getNeighbors = (row, col) => {
      const directions = row % 2 === 0 ? evenRowDirs : oddRowDirs;

      return directions
        .map(([dr, dc]) => [row + dr, col + dc])
        .filter(([r, c]) => {
          const key = `${r}-${c}`;
          return (
            r >= 0 &&
            r < tileGrid.length &&
            c >= 0 &&
            c < tileGrid[0].length &&
            tiles.includes(key)
          );
        });
    };

    const dfs = (row, col) => {
      const key = `${row}-${col}`;
      if (visited.has(key)) return false;
      visited.add(key);

      if (col === tileGrid[0].length - 1) return true;

      const neighbors = getNeighbors(row, col);
      for (const [r, c] of neighbors) {
        if (dfs(r, c)) return true;
      }
      return false;
    };

    for (let row = 0; row < tileGrid.length; row++) {
      const key = `${row}-0`;
      if (tiles.includes(key)) {
        if (dfs(row, 0)) return true;
      }
    }
    return false;
  };

  // ----------------------------
  // Tile helpers
  // ----------------------------
  const SCALE = 1;
  const BASE_WIDTH = 120;
  const BASE_HEIGHT = 104;
  const TILE_WIDTH = BASE_WIDTH * SCALE;
  const TILE_HEIGHT = BASE_HEIGHT * SCALE;

  const getTileImage = (rowIndex, colIndex) => {
    const tileKey = `${rowIndex}-${colIndex}`;

    if (reviewMode) {
      const attemptsForTile = answeredQuestions.filter((q) => q.tileKey === tileKey);
      if (attemptsForTile.length > 0) {
        const latest = attemptsForTile[attemptsForTile.length - 1];
        return latest.correct ? BLUE_TILE : WHITE_TILE;
      }
    }

    if (answeredTiles.includes(tileKey)) return BLUE_TILE;
    if (failedTiles.includes(tileKey)) return WHITE_TILE;

    if (colIndex === -1 || colIndex === 5) return BLUE_TILE;
    if (rowIndex === -1 || rowIndex === 4) return WHITE_TILE;

    return GOLD_TILE;
  };

  const getTileLetter = (tile, rowIndex, colIndex) => {
    if (colIndex === -1 || colIndex === 5 || rowIndex === -1 || rowIndex === 4) return "";
    return tile;
  };

  const openTile = (row, col) => {
    const tileKey = `${row}-${col}`;

    if (reviewMode) {
      const attemptsForTile = answeredQuestions.filter((q) => q.tileKey === tileKey);
      if (attemptsForTile.length === 0) {
        alert("No answer recorded for this tile.");
        return;
      }
      const latestIndex = Math.max(0, attemptsForTile.length - 1);
      setAttemptIndex(latestIndex);
      setSelectedTile({ row, col });
      setClickedTile(tileGrid[row][col]);
      setCurrentQuestion(attemptsForTile[latestIndex].question);
      setPlayerAnswer(attemptsForTile[latestIndex].playerAnswer || "—");
      setPlayerWorking(attemptsForTile[latestIndex].playerWorking || "—");
      setFeedback(attemptsForTile[latestIndex].correct ? "correct" : "incorrect");
      setShowModal(true);
      return;
    }

    // Normal mode: only gold tiles, only inside grid, not already answered/failed
    if (row < 0 || row >= tileGrid.length || col < 0 || col >= tileGrid[0].length) return;
    if (answeredTiles.includes(tileKey) || failedTiles.includes(tileKey)) return;

    const q = TILE_QUESTIONS[tileKey];
    if (!q) {
      alert(`⚠️ No question assigned to tile ${tileKey}`);
      return;
    }

    setSelectedTile({ row, col });
    setClickedTile(tileGrid[row][col]);
    setCurrentQuestion(q);
    setShowModal(true);
    setAttempts(0);
    setPlayerAnswer("");
    setPlayerWorking("");
    setFeedback(null);
  };

  // ----------------------------
  // Marking
  // ----------------------------
  const markAnswer = (question, rawAnswer) => {
    // Text-only question (A/B)
    if (question.expectedText) {
      const cleaned = normalizeText(rawAnswer);
      const accepted = (question.acceptedTexts || []).map(normalizeText);
      return accepted.includes(cleaned) || normalizeText(question.expectedText) === cleaned;
    }

    // Numeric question
    const studentNum = extractNumberFromMathlive(rawAnswer);
    if (studentNum === null) return false;

    // If integer required
    if (question.integerOnly) {
      const rounded = Math.round(studentNum);
      if (Math.abs(studentNum - rounded) > 1e-9) return false;
      return rounded === question.expectedValue;
    }

    const expected = Number(question.expectedValue);
    const tol = Number.isFinite(question.tolerance) ? question.tolerance : 0.01;

    return nearlyEqual(studentNum, expected, tol);
  };

  const formatExpectedForDisplay = (q) => {
    if (q.expectedText) return q.expectedText;

    const v = q.expectedValue;

    // currency-ish
    if (q.unitHint === "£") {
      return `£${Number(v).toFixed(2)}`;
    }

    if (q.unitHint === "%") {
      return `${Number(v).toFixed(2)}%`;
    }

    // years etc
    if (q.integerOnly) return `${v}`;

    return `${Number(v).toFixed(2)}`;
  };

  // ----------------------------
  // Render
  // ----------------------------
return (
  <>
    {/* 🔊 AUDIO ELEMENT — PLACE IT HERE */}
    <audio
      ref={audioRef}
      src="/BlockBusters/Blockbusters_Theme.mp3"
      loop
      preload="auto"
    />
{/* 🔊 SPEAKER TOGGLE — ALWAYS VISIBLE */}
    <button
      onClick={() => setMusicPlaying(prev => !prev)}
      style={{
        position: "fixed",
        top: "70px",
        right: "20px",
        zIndex: 2000,
        background: "transparent",
        border: "none",
        fontSize: "56px",
        cursor: "pointer",
      }}
      title={musicPlaying ? "Pause music" : "Play music"}
    >
      {musicPlaying ? "🔊" : "🔇"}
    </button>
    {stage === "welcome" ? (
      /* ================= WELCOME SCREEN ================= */
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          textAlign: "center",
          padding: "60px 20px",
          background: "linear-gradient(180deg, #f3f9ff 0%, #dfefff 100%)",
          fontFamily: "sans-serif",
        }}
  >
    <img
      src="https://firebasestorage.googleapis.com/v0/b/ryan-tutors.firebasestorage.app/o/images%2FBlockbusters_logo.png?alt=media&token=4f862aa6-e973-4929-a3c6-13eb5bce0330"
      alt="Blockbusters Logo"
      style={{ width: "380px", maxWidth: "90%", marginBottom: "20px" }}
    />

    <h1 style={{ fontSize: "42px", margin: "10px 0", color: "#2c3e50" }}>
      Blockbusters
    </h1>

<button
  onClick={() => setMusicPlaying(prev => !prev)}
  style={{
    position: "absolute",
    top: "70px",
    right: "20px",
    zIndex: 100,
    background: "transparent",
    border: "none",
    fontSize: "56px",
    cursor: "pointer",
  }}
  title={musicPlaying ? "Pause music" : "Play music"}
>
  {musicPlaying ? "🔊" : "🔇"}
</button>


   <button
  onClick={() => {
    setStage("game");

    // ✅ ensure audio is unlocked by user interaction
    audioRef.current?.play().catch(() => {});
  }}
  style={{
    padding: "16px 28px",
    fontSize: "18px",
    fontWeight: "bold",
    borderRadius: "12px",
    border: "none",
    cursor: "pointer",
    backgroundColor: "#ffd700",
    color: "#000",
  }}
>
  🎮 Start Game
</button>

  </div>
) : (
  <div
    style={{
      padding: "20px",
      fontFamily: "sans-serif",
      background:
        "linear-gradient(to bottom,rgb(83, 89, 95),rgb(183, 197, 210))",
      minHeight: "100vh",
    }}
  >
    {/* ✅ PASTE your existing game JSX content here */}
    <h1 style={{ textAlign: "center", marginBottom: "30px" }}>
      Please choose a golden tile.
    </h1>

      {/* Flip styles */}
      <style>{`
        .flip-inner {
          transition: transform 0.6s;
          transform-style: preserve-3d;
          position: relative;
          width: 100%;
          height: 100%;
        }
        .flip-front, .flip-back {
          position: absolute;
          width: 100%;
          height: 100%;
          top: 0;
          left: 0;
          backface-visibility: hidden;
        }
        .flip-back { transform: rotateY(180deg); }
      `}</style>

      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "2px",
          transform: "translate(90px, 20px)",
        }}
      >
        {[-1, 0, 1, 2, 3, 4].map((rowIndex) => (
          <div
            key={rowIndex}
            style={{
              display: "flex",
              gap: "2px",
              marginLeft: rowIndex % 2 !== 0 ? "60px" : "0",
            }}
          >
            {[-1, 0, 1, 2, 3, 4, 5].map((colIndex) => {
              const tile =
                rowIndex >= 0 && rowIndex <= 3 && colIndex >= 0 && colIndex <= 4
                  ? tileGrid[rowIndex][colIndex]
                  : "";

              const tileKey = `${rowIndex}-${colIndex}`;
              const imageSrc = getTileImage(rowIndex, colIndex);

              const isWhiteTile = imageSrc === WHITE_TILE;
              const isGoldTile = imageSrc === GOLD_TILE;

              return (
                
                <div
                  key={`${rowIndex}-${colIndex}`}
                  onClick={() => {
                    // Only allow clicks on valid interior tiles
                    if (rowIndex >= 0 && rowIndex <= 3 && colIndex >= 0 && colIndex <= 4) {
                      openTile(rowIndex, colIndex);
                    }
                  }}
                  style={{
                    perspective: "1000px",
                    width: `${TILE_WIDTH}px`,
                    height: `${TILE_HEIGHT}px`,
                    position: "relative",
                    width: `${TILE_WIDTH}px`,
                    height: `${TILE_HEIGHT}px`,

                    // (Keeping your transform positions as-is)
                    transform:
                      rowIndex === -1 && colIndex === -1
                        ? `translate(${11 * SCALE}px, ${-39 * SCALE}px)`
                        : rowIndex === 0 && colIndex === -1
                        ? `translate(${39 * SCALE}px, ${-48 * SCALE}px)`
                        : rowIndex === 1 && colIndex === -1
                        ? `translate(${8 * SCALE}px, ${-57 * SCALE}px)`
                        : rowIndex === 2 && colIndex === -1
                        ? `translate(${39 * SCALE}px, ${-69 * SCALE}px)`
                        : rowIndex === 3 && colIndex === -1
                        ? `translate(${9 * SCALE}px, ${-78 * SCALE}px)`
                        : rowIndex === 4 && colIndex === -1
                        ? `translate(${39 * SCALE}px, ${-91 * SCALE}px)`
                        : rowIndex === -1 && colIndex === 5
                        ? `translate(${-229 * SCALE}px, ${-38 * SCALE}px)`
                        : rowIndex === 0 && colIndex === 5
                        ? `translate(${-200 * SCALE}px, ${-47 * SCALE}px)`
                        : rowIndex === 1 && colIndex === 5
                        ? `translate(${-229 * SCALE}px, ${-57 * SCALE}px)`
                        : rowIndex === 2 && colIndex === 5
                        ? `translate(${-199 * SCALE}px, ${-69 * SCALE}px)`
                        : rowIndex === 3 && colIndex === 5
                        ? `translate(${-231 * SCALE}px, ${-80 * SCALE}px)`
                        : rowIndex === 4 && colIndex === 5
                        ? `translate(${-201 * SCALE}px, ${-93 * SCALE}px)`
                        : rowIndex === -1 && colIndex === 0
                        ? `translate(${-29.5 * SCALE}px, ${15 * SCALE}px)`
                        : rowIndex === -1 && colIndex === 1
                        ? `translate(${-69 * SCALE}px, ${-31 * SCALE}px)`
                        : rowIndex === -1 && colIndex === 2
                        ? `translate(${-109 * SCALE}px, ${17 * SCALE}px)`
                        : rowIndex === -1 && colIndex === 3
                        ? `translate(${-147 * SCALE}px, ${-29 * SCALE}px)`
                        : rowIndex === -1 && colIndex === 4
                        ? `translate(${-189 * SCALE}px, ${16.5 * SCALE}px)`
                        : rowIndex === 4 && colIndex === 0
                        ? `translate(${1 * SCALE}px, ${-42 * SCALE}px)`
                        : rowIndex === 4 && colIndex === 1
                        ? `translate(${-41 * SCALE}px, ${-90 * SCALE}px)`
                        : rowIndex === 4 && colIndex === 2
                        ? `translate(${-81 * SCALE}px, ${-43 * SCALE}px)`
                        : rowIndex === 4 && colIndex === 3
                        ? `translate(${-122.5 * SCALE}px, ${-89 * SCALE}px)`
                        : rowIndex === 4 && colIndex === 4
                        ? `translate(${-161 * SCALE}px, ${-43 * SCALE}px)`
                        : tile === "2"
                        ? `translate(${-39 * SCALE}px, ${-45 * SCALE}px)`
                        : tile === "6"
                        ? `translate(${-30 * SCALE}px, ${-11 * SCALE}px)`
                        : tile === "11"
                        ? `translate(${0 * SCALE}px, ${-22 * SCALE}px)`
                        : tile === "16"
                        ? `translate(${-30 * SCALE}px, ${-34 * SCALE}px)`
                        : tile === "7"
                        ? `translate(${-70 * SCALE}px, ${-57 * SCALE}px)`
                        : tile === "3"
                        ? `translate(${-79 * SCALE}px, ${2.5 * SCALE}px)`
                        : tile === "4"
                        ? `translate(${-119 * SCALE}px, ${-44 * SCALE}px)`
                        : tile === "5"
                        ? `translate(${-159 * SCALE}px, ${1 * SCALE}px)`
                        : tile === "12"
                        ? `translate(${-40 * SCALE}px, ${-70 * SCALE}px)`
                        : tile === "8"
                        ? `translate(${-109.5 * SCALE}px, ${-9 * SCALE}px)`
                        : tile === "14"
                        ? `translate(${-120 * SCALE}px, ${-67 * SCALE}px)`
                        : tile === "19"
                        ? `translate(${-152 * SCALE}px, ${-80 * SCALE}px)`
                        : tile === "17"
                        ? `translate(${-70 * SCALE}px, ${-81 * SCALE}px)`
                        : tile === "13"
                        ? `translate(${-82 * SCALE}px, ${-22 * SCALE}px)`
                        : tile === "18"
                        ? `translate(${-112 * SCALE}px, ${-34 * SCALE}px)`
                        : tile === "9"
                        ? `translate(${-149 * SCALE}px, ${-56 * SCALE}px)`
                        : tile === "10"
                        ? `translate(${-189.5 * SCALE}px, ${-10 * SCALE}px)`
                        : tile === "15"
                        ? `translate(${-162 * SCALE}px, ${-22 * SCALE}px)`
                        : tile === "20"
                        ? `translate(${-193 * SCALE}px, ${-33.5 * SCALE}px)`
                        : "none",
                    cursor:
                      rowIndex >= 0 &&
                      rowIndex <= 3 &&
                      colIndex >= 0 &&
                      colIndex <= 4 &&
                      isGoldTile &&
                      !reviewMode
                        ? "pointer"
                        : "default",
                  }}
                >
                    

                  {/* Interior tiles: flip on answered/failed */}
                  {rowIndex >= 0 && rowIndex <= 3 && colIndex >= 0 && colIndex <= 4 ? (
                    <div
                      className="flip-inner"
                      style={{
                        transform:
                          answeredTiles.includes(tileKey) || failedTiles.includes(tileKey)
                            ? "rotateY(180deg)"
                            : "none",
                      }}
                    >
                      {/* Front: GOLD */}
                      <div className="flip-front">
                        <img
                          src={GOLD_TILE}
                          alt="gold"
                          style={{ width: "100%", height: "100%", objectFit: "cover" }}
                        />
                        <div
                          style={{
                            position: "absolute",
                            inset: 0,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontWeight: "bold",
                            fontSize: "28px",
                            pointerEvents: "none",
                            color: "black",
                          }}
                        >
                          {getTileLetter(tile, rowIndex, colIndex)}
                        </div>
                      </div>

                      {/* Back: BLUE/WHITE */}
                      <div className="flip-back">
                        <img
                          src={getTileImage(rowIndex, colIndex)}
                          alt="tile"
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                            transform:
                              getTileImage(rowIndex, colIndex) === WHITE_TILE
                                ? "translateY(1.5px) scale(1.070)"
                                : "none",
                          }}
                        />
                        {/* hide number on back */}
                        <div
                          style={{
                            position: "absolute",
                            inset: 0,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontWeight: "bold",
                            fontSize: "26px",
                            pointerEvents: "none",
                            color: "transparent",
                          }}
                        >
                          {getTileLetter(tile, rowIndex, colIndex)}
                        </div>
                      </div>
                    </div>
                  ) : (
                    // Border tiles (blue/white)
                    <div style={{ position: "relative", width: "100%", height: "100%" }}>
                      <img
                        src={imageSrc}
                        alt="tile"
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                          position: "absolute",
                          top: 0,
                          left: 0,
                          transform: isWhiteTile ? "scale(1.07)" : "none",
                          transformOrigin: "center",
                          zIndex: 0,
                        }}
                      />
                      <div
                        style={{
                          position: "absolute",
                          inset: 0,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontWeight: "bold",
                          fontSize: "26px",
                          pointerEvents: "none",
                          color: "black",
                          zIndex: 1,
                        }}
                      >
                        {getTileLetter(tile, rowIndex, colIndex)}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </div>

      {/* MODAL */}
      {showModal && currentQuestion && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            backgroundColor: "rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 999,
          }}
        >
          <style>{`
            @keyframes zoomIn { from { transform: scale(0.1); opacity: 1; } to { transform: scale(1); opacity: 1; } }
            .zoom-in { animation: zoomIn 0.3s ease-out forwards; }
          `}</style>

          <div
            className="zoom-in"
            style={{
              background: "white",
              padding: "20px",
              borderRadius: "8px",
              width: "90%",
              maxWidth: "900px",
              maxHeight: "90vh",
              overflowY: "auto",
              display: "flex",
              flexDirection: "column",
              gap: "16px",
            }}
          >
            {/* Tile header */}
            {clickedTile && (
              <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
                <div
                  style={{
                    width: "70px",
                    height: "60px",
                    position: "relative",
                    flexShrink: 0,
                  }}
                >
                  <img
                    src={GOLD_TILE}
                    alt="gold tile"
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      position: "absolute",
                      top: 0,
                      left: 0,
                    }}
                  />
                  <span
                    style={{
                      position: "relative",
                      zIndex: 1,
                      fontSize: "26px",
                      fontWeight: "bold",
                      color: "black",
                      textAlign: "center",
                      lineHeight: "60px",
                      display: "block",
                    }}
                  >
                    {clickedTile}
                  </span>
                </div>

                <div style={{ fontSize: "18px", fontWeight: 700 }}>
                  {currentQuestion.topic || "Question"}
                </div>

                {/* feedback */}
                {feedback === "correct" && (
                  <div style={{ marginLeft: "8px", color: "green", fontWeight: 800 }}>
                    ✅ Correct
                  </div>
                )}
                {feedback === "incorrect" && (
                  <div style={{ marginLeft: "8px", color: "red", fontWeight: 800 }}>
                    ❌ Incorrect
                  </div>
                )}
              </div>
            )}

            {/* Review attempt navigation */}
            {reviewMode && selectedTile && (
              <div style={{ display: "flex", justifyContent: "center", gap: "14px" }}>
                <button
                  onClick={() => setAttemptIndex((prev) => Math.max(prev - 1, 0))}
                  disabled={attemptIndex === 0}
                >
                  ⬅️ Previous
                </button>
                <span style={{ fontSize: "16px", fontWeight: "bold" }}>
                  Attempt {attemptIndex + 1} of{" "}
                  {
                    answeredQuestions.filter(
                      (e) => e.tileKey === `${selectedTile.row}-${selectedTile.col}`
                    ).length
                  }
                </span>
                <button
                  onClick={() =>
                    setAttemptIndex((prev) => {
                      const total = answeredQuestions.filter(
                        (e) => e.tileKey === `${selectedTile.row}-${selectedTile.col}`
                      ).length;
                      return Math.min(prev + 1, total - 1);
                    })
                  }
                  disabled={
                    attemptIndex ===
                    answeredQuestions.filter(
                      (e) => e.tileKey === `${selectedTile.row}-${selectedTile.col}`
                    ).length -
                      1
                  }
                >
                  Next ➡️
                </button>
              </div>
            )}

            {/* Content */}
            <div style={{ display: "flex", gap: "20px" }}>
              {/* Left: question + answer */}
              <div style={{ flex: 1 }}>
                <div ref={questionRef}>
                  {Array.isArray(currentQuestion.questionParagraphs) &&
  currentQuestion.questionParagraphs.map((p, i) => {
    // 🖼️ Image block
    if (p && typeof p === "object" && p.type === "image") {
      return (
        <div key={i} style={{ margin: "14px 0", textAlign: "center" }}>
          <img
            src={p.src}
            alt={p.alt || ""}
            style={{
              maxWidth: p.maxWidth || "100%",
              width: "100%",
              borderRadius: "8px",
            }}
          />
        </div>
      );
    }

    // 📐 LaTeX (only if string)
    if (typeof p === "string" && isLatex(p)) {
      return (
        <div key={i} style={{ fontSize: "20px", marginBottom: "10px" }}>
          <BlockMath math={p} />
        </div>
      );
    }

    // 📝 Normal text
    return (
      <div
        key={i}
        style={{
          fontSize: "18px",
          marginBottom: "10px",
          whiteSpace: "pre-wrap",
          lineHeight: "1.6",
        }}
      >
        {String(p)}
      </div>
    );
  })}

                </div>

                {/* Answer input */}
<div style={{ display: "flex", alignItems: "center", gap: "10px", marginTop: "10px" }}>
  {/* £ on the LEFT (only for currency questions) */}
  {currentQuestion.unitHint === "£" && (
    <span style={{ fontWeight: 700, fontSize: "18px" }}>£</span>
  )}

  <math-field
    ref={mathFieldRef}
    readOnly={reviewMode}
    style={{
      width: "320px",
      fontSize: "18px",
      border: "2px solid #ccc",
      borderRadius: "8px",
      padding: "8px",
      boxSizing: "border-box",
    }}
    placeholder="Enter your answer here"
    onInput={(e) => setPlayerAnswer(e.target.value)}
  ></math-field>

  {/* Units on the RIGHT for everything else */}
  {currentQuestion.unitHint && currentQuestion.unitHint !== "£" && (
    <span style={{ fontWeight: 700, fontSize: "16px" }}>
      {currentQuestion.unitHint}
    </span>
  )}
</div>


                {/* Correct answer reveal (after 2 failed attempts) */}
                {feedback === "incorrect" && attempts >= 1 && (
                  <div style={{ marginTop: "10px", color: "red", fontWeight: 700 }}>
                    Correct answer:{" "}
                    <span style={{ color: "#111" }}>{formatExpectedForDisplay(currentQuestion)}</span>
                  </div>
                )}
              </div>

              {/* Right: working */}
              <div style={{ flex: 1 }}>
                <textarea
                  readOnly={reviewMode}
                  value={playerWorking}
                  onChange={(e) => setPlayerWorking(e.target.value)}
                  placeholder="Show your working here"
                  style={{
                    width: "100%",
                    padding: "10px",
                    fontSize: "16px",
                    border: "2px solid black",
                    borderRadius: "6px",
                    resize: "none",
                    height: `${Math.min(Math.max(questionHeight, 120), 320)}px`,
                    boxSizing: "border-box",
                  }}
                />
              </div>
            </div>

            {/* Buttons */}
            <div style={{ marginTop: "10px", display: "flex", justifyContent: "space-between" }}>
              <button
                onClick={() => {
                  setShowModal(false);
                  setPlayerAnswer("");
                  setPlayerWorking("");
                  setFeedback(null);
                }}
              >
                Cancel
              </button>

              {!reviewMode && (
                <button
                  onClick={async () => {
                    if (!currentQuestion || !selectedTile) return;

                    const tileKey = `${selectedTile.row}-${selectedTile.col}`;
                    const isCorrect = markAnswer(currentQuestion, playerAnswer);

                    // Save attempt locally + firebase
                    setAnsweredQuestions((prev) => [
                      ...prev,
                      {
                        tileKey,
                        questionId: currentQuestion.id,
                        question: currentQuestion,
                        playerAnswer,
                        playerWorking,
                        correct: isCorrect,
                        tileValue: clickedTile,
                        submittedAt: new Date(),
                      },
                    ]);

                    try {
                      const submissionId = `${studentUID}_${assignmentId}`;
                      const submissionRef = doc(db, "studentAssignmentSubmissions", submissionId);

                      await updateDoc(submissionRef, {
                        answers: arrayUnion({
                          tileKey,
                          questionId: currentQuestion.id,
                          tileValue: clickedTile,
                          playerAnswer,
                          playerWorking,
                          correct: isCorrect,
                          submittedAt: new Date(),
                        }),
                      });
                    } catch (e) {
                      console.error("❌ Failed to save attempt:", e);
                    }

                    if (isCorrect) {
                      setFeedback("correct");

                      setTimeout(async () => {
                        setAnsweredTiles((prev) => {
                          const updated = [...prev, tileKey];

                          // Check win
                          if (checkForWin(updated)) {
                            setHasWon(true);

                            // Save completion timestamp
                            (async () => {
                              try {
                                const submissionId = `${studentUID}_${assignmentId}`;
                                const submissionRef = doc(db, "studentAssignmentSubmissions", submissionId);
                                await updateDoc(submissionRef, { timeSubmitted: serverTimestamp() });
                              } catch (err) {
                                console.error("❌ Failed to update timeSubmitted:", err);
                              }
                            })();
                          }

                          return updated;
                        });

                        setShowModal(false);
                        setPlayerAnswer("");
                        setPlayerWorking("");
                        setFeedback(null);
                        setClickedTile(null);
                      }, 1400);
                    } else {
                      setFeedback("incorrect");

                      setTimeout(() => {
                        setFeedback(null);
                        setAttempts((prev) => {
                          const newAttempts = prev + 1;

                          // After 2 tries, fail tile (white)
                          if (newAttempts >= 2) {
                            setFailedTiles((p) => [...p, tileKey]);
                            setShowModal(false);
                            setClickedTile(null);
                            setPlayerAnswer("");
                            setPlayerWorking("");
                          }
                          return newAttempts;
                        });
                      }, 2200);
                    }
                  }}
                >
                  Submit
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* WIN overlay */}
      {hasWon && !reviewMode && (
        <>
          <Confetti width={width} height={height} />
          <div
            style={{
              position: "fixed",
              inset: 0,
              backgroundColor: "rgba(0, 0, 0, 0.6)",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              color: "pink",
              fontSize: "58px",
              fontWeight: "bold",
              zIndex: 9999,
              textAlign: "center",
              padding: "20px",
            }}
          >
            YOU WIN!
            <img
              src="https://firebasestorage.googleapis.com/v0/b/ryan-tutors.firebasestorage.app/o/images%2FBlockbusters%20Bitomoji_celebrate.png?alt=media&token=c4ab9678-43d0-4413-873a-1fd61766702a"
              alt="Bitmoji celebrate"
              style={{ marginTop: "30px", maxHeight: "390px", borderRadius: "12px" }}
            />
            <button
              onClick={() => setReviewMode(true)}
              style={{
                marginTop: "30px",
                fontSize: "20px",
                padding: "12px 24px",
                backgroundColor: "#ffd700",
                border: "none",
                borderRadius: "8px",
                cursor: "pointer",
                color: "black",
              }}
            >
              🔍 Review Questions
            </button>
          </div>
        </>
      )}

      {/* REVIEW LIST */}
      {reviewMode && (
        <div
          style={{
            backgroundColor: "#fff",
            color: "#000",
            padding: "20px",
            borderRadius: "12px",
            maxWidth: "900px",
            margin: "40px auto",
            fontFamily: "sans-serif",
          }}
        >
          <h2>📋 Review Questions</h2>

          {answeredQuestions.map((entry, index) => (
            <div
              key={index}
              style={{
                backgroundColor: entry.correct ? "#e6ffe6" : "#ffe6e6",
                border: "1px solid #ccc",
                borderRadius: "10px",
                marginBottom: "16px",
                padding: "16px",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                <div style={{ width: "55px", height: "45px", position: "relative", flexShrink: 0 }}>
                  <img
                    src={GOLD_TILE}
                    alt="Tile"
                    style={{ width: "100%", height: "100%", objectFit: "cover", position: "absolute" }}
                  />
                  <span
                    style={{
                      position: "relative",
                      zIndex: 1,
                      fontSize: "20px",
                      fontWeight: "bold",
                      color: "black",
                      textAlign: "center",
                      lineHeight: "45px",
                      display: "block",
                    }}
                  >
                    {entry.tileValue || "?"}
                  </span>
                </div>

                <div style={{ fontSize: "18px", fontWeight: "bold" }}>
                  {(entry.question?.topic || "Question")}
                </div>
              </div>

              <div style={{ marginTop: "10px" }}>
             {entry.question?.questionParagraphs?.map((p, i) => {
  // 🖼️ Image block
  if (p && typeof p === "object" && p.type === "image") {
    return (
      <div key={i} style={{ margin: "10px 0", textAlign: "center" }}>
        <img
          src={p.src}
          alt={p.alt || ""}
          style={{
            maxWidth: p.maxWidth || "100%",
            width: "100%",
            borderRadius: "8px",
          }}
        />
      </div>
    );
  }

  // 📐 LaTeX
  if (typeof p === "string" && isLatex(p)) {
    return (
      <div key={i} style={{ margin: "6px 0" }}>
        <BlockMath math={p} />
      </div>
    );
  }

  // 📝 Normal text
  return (
    <div key={i} style={{ margin: "6px 0" }}>
      {String(p)}
    </div>
  );
})}

              </div>

              <p style={{ marginTop: "10px", fontSize: "16px" }}>
                💬 <strong>Your Answer:</strong>{" "}
                <span style={{ fontWeight: 700 }}>{entry.playerAnswer || "—"}</span>{" "}
                {entry.correct ? "✅" : "❌"}
              </p>

              {!entry.correct && (
                <p style={{ marginTop: "6px", fontSize: "16px" }}>
                  ✔️ <strong>Correct Answer:</strong>{" "}
                  <span style={{ fontWeight: 800 }}>{formatExpectedForDisplay(entry.question)}</span>
                </p>
              )}
            </div>
          ))}

          <button
            onClick={() => setReviewMode(false)}
            style={{
              fontSize: "16px",
              padding: "10px 20px",
              backgroundColor: "#ccc",
              border: "none",
              borderRadius: "6px",
              cursor: "pointer",
            }}
          >
            🔙 Back to Game
          </button>
        </div>
           )}
    </div>  
    )}      
  </>       
);

}

export default BlockbustersGrid;
